import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { FormControl, InputLabel, MenuItem, Select, SelectChangeEvent, Skeleton } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { MEASUREMENT_DATE_SLUG } from 'dashboard409a/common/constants';
import { useCompanyMeasurementDates } from 'dashboard409a/hooks/useCompanyMeasurementDates';
import { updateGlobalState } from 'dashboard409a/states/features/global';
import { AppDispatchType, RootStateType } from 'dashboard409a/states/store';
import { formatMD, getUniqueListBy, useQueryParams } from 'dashboard409a/utilities';
import { toString } from 'utilities';
import useStyles from './styles';
import { IDateFilter, IOptions } from './types';

const DateFilter: FC<IDateFilter> = props => {
  const { label, filterId, isLoading } = props;

  const [selectedValue, setSelectedValue] = useState('');

  const classes = useStyles();
  const { getQuery, setQuery } = useQueryParams();

  const { currentCompany } = useSelector((state: RootStateType) => state.global);
  const dispatch = useDispatch<AppDispatchType>();

  const queryMeasurementDate = getQuery.get('md');

  // Fetch company measurement dates using the new hook
  const { 
    data: companyMeasurementDates = [], 
    isLoading: isLoadingMeasurementDates 
  } = useCompanyMeasurementDates(currentCompany?.id?.toString());

  // Memoize options to prevent infinite re-renders
  const options = useMemo(() => {
    if (companyMeasurementDates.length === 0) {
      return [];
    }

    const allOptions = companyMeasurementDates.map(
      measurementDate =>
        ({
          id: measurementDate.id!,
          date: measurementDate.date,
          slug: measurementDate.slug ?? `${MEASUREMENT_DATE_SLUG}-${measurementDate.date}`,
          companyMeasurementDateId: measurementDate.cmd_id!,
        } as IOptions)
    );

    return getUniqueListBy<IOptions>(allOptions, 'id').sort((a, b) => b.date.localeCompare(a.date));
  }, [companyMeasurementDates]);

  const setLastMeasurementDate = useCallback(() => {
    if (companyMeasurementDates.length > 0) {
      const recentMD = options.length > 0 ? options[0] : undefined;

      if (recentMD) {
        const queryMD = `${recentMD.date}-${recentMD.id}`;

        setQuery('md', queryMD);
        dispatch(
          updateGlobalState({
            currentMeasurementDateId: recentMD.id,
            currentCompanyMeasurementDateId: recentMD.companyMeasurementDateId,
            currentMeasurementDate: { ...recentMD, date: recentMD.date, cmd_id: recentMD.companyMeasurementDateId },
          })
        );
      }
    }
  }, [companyMeasurementDates, dispatch, options, setQuery]);

  // get/set MD from query params
  useEffect(() => {
    if (options.length === 0) {
      return;
    }

    if (queryMeasurementDate) {
      // Try to find by full slug format first (measurement-date-YYYY-MM-DD)
      let currentMD = options.find(option => option.slug === `${MEASUREMENT_DATE_SLUG}-${queryMeasurementDate}`);
      
      // If not found, try to find by date format only (YYYY-MM-DD) - for Attio navigation
      currentMD ??= options.find(option => option.date === queryMeasurementDate);

      if (currentMD) {
        setSelectedValue(toString(currentMD.id));
        dispatch(
          updateGlobalState({
            currentMeasurementDate: { ...currentMD, date: currentMD.date, cmd_id: currentMD.companyMeasurementDateId },
            currentMeasurementDateId: currentMD.id,
            currentCompanyMeasurementDateId: currentMD.companyMeasurementDateId,
          })
        );
      } else {
        setLastMeasurementDate();
      }
    } else {
      setLastMeasurementDate();
    }
  }, [dispatch, options, queryMeasurementDate, setLastMeasurementDate]);

  const renderValue = (value: unknown) => {
    const currentOption = options.find(option => toString(option.id) === toString(value as string | number));

    if (options.length > 0 && currentOption) {
      return formatMD({ date: currentOption.date });
    }

    return `No ${label.toLowerCase()}s available`;
  };

  const onMeasurementDateChange = (event: SelectChangeEvent<string>) => {
    const measurementDateId = event.target.value;

    if (measurementDateId) {
      const selectedMD = options.find(md => md.id === Number(measurementDateId));

      if (selectedMD) {
        const selectedMDQuery = `${selectedMD.date}-${selectedMD.id}`;
        setQuery('md', selectedMDQuery);
      }
    }
  };

  // Loading
  if (isLoading || isLoadingMeasurementDates) {
    return <Skeleton variant="text" width={150} height={50} />;
  }

  return (
    <FormControl className={classes.formControl}>
      <InputLabel id={`${filterId}-label`} className={classes.label} shrink>
        {label}
      </InputLabel>
      <Select
        variant="standard"
        classes={{ root: classes.select }}
        className={classes.select}
        displayEmpty
        id={filterId}
        onChange={onMeasurementDateChange}
        renderValue={renderValue}
        value={selectedValue}>
        <MenuItem selected disabled value=" ">
          Select a {label}
        </MenuItem>
        {options.map(({ id, date }) => 
          <MenuItem value={toString(id)} key={toString(id)} id={`${filterId}-option-${id + 1}`}>
            {formatMD({ date })}
          </MenuItem>
        )}
      </Select>
    </FormControl>
  );
};

export default DateFilter;
